import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { backOff } from 'exponential-backoff';
import { StatusCodes } from 'http-status-codes';

import ApiException from './ApiException';


export const defaultConfig: AxiosRequestConfig = {
  headers: { 'content-type': 'application/json; charset=utf-8' },
  withCredentials: true,
};

type AxiosMethod = (config?: AxiosRequestConfig) => Promise<AxiosResponse<any>>;
type ApiServiceMethod = (url: string, config: AxiosRequestConfig) => Promise<AxiosResponse<any>>;

export default class ApiService {

  static async #sendRequest(axiosMethod: AxiosMethod, config: AxiosRequestConfig = {}) {
    const { headers, ...restConfig } = config;
    const configWithDefaults: AxiosRequestConfig = {
      withCredentials: defaultConfig.withCredentials,
      headers: {
        ...defaultConfig.headers,
        ...headers,
      },
      ...restConfig,
    };

    try {
      return await axiosMethod(configWithDefaults);
    } catch (error) {
      const {
        message,
        config: { url } = { url: undefined },
        response = {},
      } = error;
      const { data, status } = response;
      throw new ApiException(
        message,
        url,
        status,
        data,
        JSON.stringify(error),
        response,
      );
    }
  }

  static get(url: string, config: AxiosRequestConfig = {}) {
    const { data, ...restConfig } = config;

    return this.#sendRequest((configWithDefaults) => axios.get(url, configWithDefaults), restConfig);
  }

  static head(url: string, config: AxiosRequestConfig = {}) {
    const { data, ...restConfig } = config;

    return this.#sendRequest((configWithDefaults) => axios.head(url, configWithDefaults), restConfig);
  }

  static delete(url: string, config: AxiosRequestConfig = {}) {
    const { data, ...restConfig } = config;

    return this.#sendRequest((configWithDefaults) => axios.delete(url, configWithDefaults), restConfig);
  }

  static post(url: string, config: AxiosRequestConfig = {}) {
    const { data = null, ...restConfig } = config;

    return this.#sendRequest((configWithDefaults) => axios.post(url, data, configWithDefaults), restConfig);
  }

  static put(url: string, config: AxiosRequestConfig = {}) {
    const { data = null, ...restConfig } = config;

    return this.#sendRequest((configWithDefaults) => axios.put(url, data, configWithDefaults), restConfig);
  }

  static patch(url: string, config: AxiosRequestConfig = {}) {
    const { data = null, ...restConfig } = config;

    return this.#sendRequest((configWithDefaults) => axios.patch(url, data, configWithDefaults), restConfig);
  }

  static sendRequestWithRetries(
    method: ApiServiceMethod,
    url: string,
    config: AxiosRequestConfig,
    numOfAttempts: number = 3,
    retriableStatusCodes: StatusCodes[] = [
      StatusCodes.INTERNAL_SERVER_ERROR,
      StatusCodes.BAD_GATEWAY,
      StatusCodes.SERVICE_UNAVAILABLE,
      StatusCodes.GATEWAY_TIMEOUT,
    ],
  ) {
    return backOff(
      () => method.call(ApiService, url, config),
      {
        jitter: 'full',
        numOfAttempts,
        retry: (error) => retriableStatusCodes.includes(error.status),
      }
    );
  }

  static substitute(url: string, props: Record<string, string>) {
    if (!props) {
      return url;
    }

    let substituted = url;
    for (const prop in props) {
      if (Object.prototype.hasOwnProperty.call(props, prop)) {
        const regex = new RegExp(`{${prop}}`, 'g');
        substituted = substituted.replace(regex, props[prop]);
      }
    }

    return substituted;
  }
}
